import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Select,
  MenuItem,
  Paper,
  useMediaQuery,
} from "@mui/material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import { useNavigate, useSearchParams } from "react-router-dom";
import { usePTagForm } from "../contexts/PTagFormContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getShopInfo } from "../services/api";
import { validatePhoneNumber, validateEmail } from "../utils/validation";
import { last, transform } from "lodash";
import { useTranslation } from "react-i18next";

export default function PTagAirPayment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { form, setForm, addForm, totalForms, activeFormIndex, setActiveFormIndex, forms, incrementFormsAdded } = usePTagForm();
  const [shopInfo, setShopInfo] = useState(null);
  const [shop, setShop] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';
  const isMobile = useMediaQuery("(max-width:900px)");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!edit) {
      addForm();
      setActiveFormIndex(totalForms);
      setShowPopup(true);
    }
    getShopInfo().then((res) => {
      setShopInfo(res.data.shopInfo);
    });
  }, []); // Empty dependency array to run only on mount

  // Function to close the popup
  const handleClosePopup = () => {
    setShowPopup(false);
  };


  useEffect(() => {
    setFieldErrors((prev) => {
      const newErrors = { ...prev };

      // Phone number error translation
      if (
        prev.phoneNumber === "請輸入聯絡電話" ||
        prev.phoneNumber === "Please enter phone number"
      ) {
        newErrors.phoneNumber = t('EnterPhoneNumber');
      } else if (
        prev.phoneNumber === "聯絡電話只能包含數字" ||
        prev.phoneNumber === "Phone number can only contain numbers"
      ) {
        newErrors.phoneNumber = t('OnlyNumbers');
      } else if (
        prev.phoneNumber === "請輸入有效的電話號碼" ||
        prev.phoneNumber === "Please enter a valid phone number"
      ) {
        newErrors.phoneNumber = t('InvalidPhoneNumber');
      }

      // Email error translation
      if (
        prev.email === "請輸入電郵地址" ||
        prev.email === "Please enter email"
      ) {
        newErrors.email = t('EnterEmail');
      } else if (
        prev.email === "請輸入有效的電郵地址" ||
        prev.email === "Please enter a valid email"
      ) {
        newErrors.email = t('InvalidEmail');
      }

      // Last name error translation
      if (
        prev.lastName === "請輸入收件人姓名" ||
        prev.lastName === "Please enter recipient's name"
      ) {
        newErrors.lastName = t('EnterRecipientName');
      }

      // Pet name error translation
      if (
        prev.petName === "請輸入寵物名稱" ||
        prev.petName === "Please enter pet's name"
      ) {
        newErrors.petName = t('EnterPetName');
      }

      return newErrors;
    });
  }, [t]);

  // Placeholder prices
  const shipping = 50;

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));

    // Real-time validation for specific fields
    if (field === "phoneNumber") {
      const phoneError = validatePhoneNumber(value, t);
      setFieldErrors((prev) => ({
        ...prev,
        phoneNumber: phoneError,
      }));
    } else if (field === "email") {
      const emailError = validateEmail(value, t);
      setFieldErrors((prev) => ({
        ...prev,
        email: emailError,
      }));
    } else if (field === "lastName") {
      // Validate lastName name
      if (!value.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          lastName: t('EnterRecipientName'),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          lastName: "",
        }));
      }
    } else if (field === "petName") {
      // Validate pet name
      if (!value.trim()) {
        setFieldErrors((prev) => ({
          ...prev,
          petName: t('EnterPetName'),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          petName: "",
        }));
      }
    } else if (field === "delivery") {
      setForm(prev => ({
        ...prev,
        delivery: value,
        ...(value === "SF-HOME-DELIVERY"
          ? { netCode: "", serviceName: "", shippingAddress: "", attrName: "" }
          : {})
      }));
    } else {
      // Clear error when field is filled
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validation: all required fields must be filled and no validation errors
  const isFormValid =
    // form.lastName &&
    // form.phoneNumber &&
    // form.email &&
    // form.delivery &&
    // form.paymentWay &&
    form.petName &&
    // form.address || form.shippingAddress &&
    // !fieldErrors.phoneNumber &&
    // !fieldErrors.email &&
    // !fieldErrors.lastName &&
    !fieldErrors.petName;

  const verifyCoupon = () => {
    form.coupon = form.coupon.toUpperCase();
    const shop = shopInfo.find((shop) => shop.shopCode === form.coupon);
    if (shop) {
      setShop(shop);
      setSuccess(t('validCode'));
      setError("");
    } else {
      setShop(null);
      setError(t('invalidCode'));
      setSuccess("");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setFileError(t('invalidFileType') || 'Please upload a PDF or image file (JPG, PNG, GIF)');
      return;
    }

    // Validate file size (2-10 MB)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      setFileError(t('fileTooLarge') || 'File size must not exceed 10 MB');
      return;
    }

    // File is valid
    setUploadedFile(file);
    setFileError("");
    setForm((prev) => ({ ...prev, discountProof: file }));
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    setFileError("");
    setForm((prev) => ({ ...prev, discountProof: null }));
  };

  // Image compression utility
  const compressImage = async (file) => {
    const fileSizeMB = file.size / (1024 * 1024);
    
    let quality;
    if (fileSizeMB <= 3) {
      quality = 1; 
    } else if (fileSizeMB <= 5) {
      quality = 0.5; 
    } else if (fileSizeMB <= 7) {
      quality = 0.4; 
    } else {
      quality = 0.2; 
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`Compressed ${file.name}: ${fileSizeMB.toFixed(2)}MB → ${(blob.size / (1024 * 1024)).toFixed(2)}MB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent overlay
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000, // Ensure popup is above other content
    overflow: "auto",
  };

  const popupContentStyle = {
    backgroundColor: "#FFFFFF",
    padding: "40px",
    borderRadius: "30px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    width: isMobile ? "93%" : "1080px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    marginTop: isMobile ? 200 : 50,
  };

  const popupContentStyle2 = {
    backgroundColor: "#FFFFFF",
    padding: "30px",
    borderRadius: "30px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    width: isMobile ? "93%" : "1200px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  };

  const closeButtonStyle = {
    position: "absolute", top: 0, right: isMobile ? "-10px" : 0,
    cursor: "pointer",
  };

  return (
    <Box
      sx={{ background: "#FCF8F3", minHeight: "100vh", pt: 10, position: "relative" }}
    >
      <Navbar />
      <Button
        sx={{ color: "black", minWidth: 0, position: "absolute", left: isMobile ? 10 : 80 }}
        onClick={() => edit ? navigate("/shopping_cart") : navigate("/ptag-air")}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="24" viewBox="0 0 14 24" fill="none">
          <path d="M12 22L2 12L12 2" stroke="#050505" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Button>
      <Box
        sx={{
          maxWidth: 1080,
          width: "100%",
          mx: "auto",
          px: { xs: 1, sm: 2 },
          pt: { xs: 2, md: 6 },
          pb: { xs: 4, md: 8 },
        }}
      >

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 4, md: 6 },
            width: "100%",
          }}
        >
          {/* Left: Order Summary */}
          <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 4, md: 0 }, mt: 4, px: 1 }}>
            <Paper elevation={0} sx={{ background: "transparent", p: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <img
                  src="/icons/ptagair-2.png"
                  alt="PTag Air"
                  style={{
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 5 : 16 }}>
                  <Box>
                    <Typography fontWeight={700}>
                      PTag <span style={{ color: "#2196f3" }}>Air</span>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                        <g clipPath="url(#clip0_5995_46606)">
                          <path d="M13.5801 6.84285V7.41075C13.5793 8.74187 13.1483 10.0371 12.3512 11.1032C11.5542 12.1694 10.4339 12.9493 9.15744 13.3267C7.88094 13.7042 6.51663 13.6588 5.268 13.1975C4.01936 12.7362 2.9533 11.8836 2.2288 10.767C1.50429 9.65027 1.16017 8.32931 1.24776 7.00107C1.33534 5.67282 1.84993 4.40848 2.71479 3.3966C3.57965 2.38471 4.74844 1.6795 6.04684 1.38615C7.34524 1.09279 8.70368 1.22701 9.91956 1.76877" stroke="#969696" strokeWidth="1.23457" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M13.5794 2.46875L7.40654 8.64776L5.55469 6.79591" stroke="#969696" strokeWidth="1.23457" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                        <defs>
                          <clipPath id="clip0_5995_46606">
                            <rect width="14.8148" height="14.8148" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <Typography fontSize={14} color="text.secondary">
                        {t('white')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography>
                      HKD $199
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <>
                {/* Coupon */}
                <Box sx={{ mt: 4 }}>
                  <Typography fontWeight={700} fontSize={12} mb={1}>
                    {t('discountCode')}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, }}>
                    <TextField
                      size="small"
                      placeholder={t('discountCode')}
                      value={form.coupon}
                      onChange={handleChange("coupon")}
                      sx={{
                        flex: 1, background: "#fff",
                        fontSize: 12, // This sets the input text font size
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: 12, // Sets the placeholder font size
                        },
                      }}
                      inputProps={{
                        style: {
                          textTransform: "uppercase",
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      sx={{
                        background: "#D9D9D9",
                        color: "#333",
                        boxShadow: "none",
                      }}
                      onClick={verifyCoupon}
                    >
                      {t('apply')}
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <label htmlFor="discount-file-upload">
                      <input
                        id="discount-file-upload"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.gif"
                        style={{ display: "none" }}
                        onChange={handleFileUpload}
                      />
                      <Box sx={{ 
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.7 }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M17 8L12 3L7 8" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 3V15" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Box>
                    </label>
                    <Typography fontSize={13}>
                      {t('discountInfo')}
                    </Typography>
                  </Box>

                  {/* File upload preview/status */}
                  {uploadedFile && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      backgroundColor: '#E6FFF0', 
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M13 2V9H20" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Box>
                          <Typography fontSize={12} fontWeight={600}>
                            {uploadedFile.name}
                          </Typography>
                          <Typography fontSize={10} color="text.secondary">
                            {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                          </Typography>
                        </Box>
                      </Box>
                      <Box 
                        sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                        onClick={removeUploadedFile}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Box>
                    </Box>
                  )}

                  {fileError && (
                    <Typography color="error" fontSize={12} mt={1} sx={{ backgroundColor: '#FFE6E6', p: 1, borderRadius: 1 }}>
                      {fileError}
                    </Typography>
                  )}


                </Box>
                {success && (
                  <Typography color="success" fontSize={13} mt={1} mb={1}>
                    {success}
                  </Typography>
                )}
                {error && (
                  <Typography color="error" fontSize={13} mt={1} mb={1} backgroundColor="#FFE6E6">
                    {error}
                  </Typography>
                )}
                {/* Price Summary */}
                <Box sx={{ mt: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography color="text.secondary">{t('subtotal')}</Typography>
                    <Typography>HKD ${shop ? shop.price : 199}</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography color="text.secondary">{t('shippingFee')}</Typography>
                    <Typography>HKD ${shipping}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography fontWeight={700}>{t('total')}</Typography>
                    <Typography fontWeight={700} fontSize={20} color="#050505">
                      HKD ${(shop ? shop.price : 199) + shipping}
                    </Typography>
                  </Box>
                </Box>
              </>
            </Paper>
          </Box>

          {/* Vertical Divider for desktop only */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" }, mx: 3, minHeight: 400 }}
          />

          {/* Horizontal divider for mobile only */}
          <Divider
            orientation="horizontal"
            flexItem
            sx={{ display: { xs: "block", md: "none" } }}
          />

          {/* Right: Form */}
          <Box
            sx={{ flex: 1, minWidth: 0, width: "100%", mt: { xs: 4, md: 0 }, px: 1 }}
          >
            <Paper elevation={0} sx={{ background: "transparent", p: 0 }}>
              {/* Conditionally show 打印內容 if version is custom */}
              <Box mb={4}>
                <Typography fontWeight={700} fontSize={26} mb={2}>
                  {t('PtagAirPrintContent')}
                </Typography>
                <Typography fontWeight={400} fontSize={12} mb={2}>
                  {t('petName')}
                </Typography>
                <TextField
                  placeholder={t('petName')}
                  fullWidth
                  value={form.petName}
                  onChange={handleChange("petName")}
                  error={!!fieldErrors.petName}
                  helperText={fieldErrors.petName}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                />
                <Box sx={{ mb: 2 }}>
                  <Typography fontSize={15} mb={1} style={{ display: "flex", gap: 5 }}>
                    {t('photo')}
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ cursor: "pointer" }} width="20" height="20" viewBox="0 0 20 20" fill="none" onClick={() => setShowPopup(true)}>
                      <g clipPath="url(#clip0_5995_45906)">
                        <path d="M10.0013 18.3307C14.6037 18.3307 18.3346 14.5998 18.3346 9.9974C18.3346 5.39502 14.6037 1.66406 10.0013 1.66406C5.39893 1.66406 1.66797 5.39502 1.66797 9.9974C1.66797 14.5998 5.39893 18.3307 10.0013 18.3307Z" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7.57422 7.49852C7.77014 6.94158 8.15685 6.47194 8.66585 6.1728C9.17485 5.87365 9.7733 5.7643 10.3552 5.86411C10.9371 5.96393 11.4649 6.26646 11.8451 6.71813C12.2253 7.1698 12.4334 7.74146 12.4326 8.33185C12.4326 9.99852 9.93255 10.8319 9.93255 10.8319" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 14.1641H10.0083" stroke="#969696" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_5995_45906">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </Typography>
                  <label htmlFor="pet-photo-upload">
                    <Box
                      sx={{
                        width: "100%",
                        minHeight: 240,
                        background: "#fff",
                        border: "1px dashed #ccc",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {form.photo ? (
                        <img
                          src={
                            typeof form.photo === "string"
                              ? form.photo
                              : URL.createObjectURL(form.photo)
                          }
                          alt="預覽"
                          style={{
                            width: "100%",
                            height: 300,
                            objectFit: "contain",
                            borderRadius: 8,
                            border: "30px solid white",
                            backgroundColor: "#fff"
                          }}
                        />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                          <rect width="25" height="25" rx="12.5" fill="#FFF0CD" />
                          <path d="M12.5 8.33594V16.6693" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8.33203 12.5H16.6654" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      <input
                        id="pet-photo-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            try {
                              // Compress the image
                              const compressedFile = await compressImage(file);
                              setForm((prev) => ({ ...prev, photo: compressedFile }));
                            } catch (error) {
                              console.error("Error processing image:", error);
                              // Error already handled in compressImage
                            }
                          }
                        }}
                      />
                    </Box>
                  </label>
                  <Typography color="text.secondary" fontSize={13} mt={1} style={{ color: "#969696" }}>
                    {t('photoInfo')}
                  </Typography>
                </Box>
              </Box>
              {/* Conditionally show 收件資料 and 付款資料 if all radio groups are selected */}
              <>
                <Box mb={4}>
                  <Button
                    variant="contained"
                    sx={{
                      background: "#FFB60C",
                      color: "#050505",
                      fontWeight: 700,
                      fontSize: 16,
                      borderRadius: 2,
                      px: 6,
                      py: 1.5,
                      mt: 2,
                      boxShadow: "none",
                      "&:hover": { background: "#FFB60C" },
                    }}
                    fullWidth
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        option: "PTagAir",
                        color: "白色",
                        price: shop ? shop.price : 199,
                        size: '25',
                      }));
                      if (!edit) {
                        incrementFormsAdded();
                      }
                      navigate("/shopping_cart");
                    }}
                    disabled={!isFormValid}
                  >
                    {t('AddtoCart')}
                  </Button>
                </Box>
              </>
            </Paper>
          </Box>
        </Box>
      </Box>
      <Footer />
      {showPopup && (
        <div style={popupOverlayStyle}>
          <div style={popupContentStyle}>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative", width: "100%", paddingBottom: "20px" }}>
                <div style={{ color: "#FFB60C", fontSize: isMobile ? 18 : 34, fontWeight: 700, textAlign: "center", }}>{t("popupTitle")}</div>
                <button style={closeButtonStyle} onClick={handleClosePopup}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                    <path d="M22.5 7.5L7.5 22.5" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.5 7.5L22.5 22.5" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <div style={{ color: "#969696", fontSize: isMobile ? 13 : 21, fontWeight: 400, textAlign: "center", }}>
                  {t("popupContent1")}
                </div>
                <div style={{ color: "#F10000", fontSize: isMobile ? 13 : 21, fontWeight: 400, textAlign: "center", }}>
                  {t("popupContent2")}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "center", alignItems: "center", gap: 50 }}>
              <div style={{ width: isMobile ? 300 : 400, height: isMobile ? 300 : 400, backgroundColor: "#E6FFF0", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: isMobile ? 20 : 40, borderRadius: 19 }}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>
                  <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700 }}>{t("popupCorrectPhoto")}</div>
                  <div style={{ width: isMobile ? 20 : "auto" }}>
                    <img src="/icons/Correctcircle.png" alt="" />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 20 }}>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupCorrectImg1.png" alt="" /></div>
                  <div style={{ width: isMobile ? 40 : "auto" }}><img src="/icons/GreenArrow.png" alt="" /></div>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupPtag1.png" alt="" /></div>
                </div>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 20 }}>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupCorrectImg2.png" alt="" /></div>
                  <div style={{ width: isMobile ? 40 : "auto" }}><img src="/icons/GreenArrow.png" alt="" /></div>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupPtag2.png" alt="" /></div>
                </div>
              </div>
              <div style={{ width: isMobile ? 300 : 400, height: isMobile ? 300 : 400, backgroundColor: "rgba(255, 126, 83, 0.15)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: isMobile ? 20 : 40, borderRadius: 19 }}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>
                  <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700 }}>{t("popupWrongPhoto")}</div>
                  <div style={{ width: isMobile ? 30 : "auto" }}>
                    <img src="/icons/WrongCross.png" alt="" />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 20 }}>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupWrongImg1.png" alt="" /></div>
                  <div style={{ width: isMobile ? 40 : "auto" }}><img src="/icons/RedArrow.png" alt="" /></div>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupPtag3.png" alt="" /></div>
                </div>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 20 }}>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupWrongImg2.png" alt="" /></div>
                  <div style={{ width: isMobile ? 40 : "auto" }}><img src="/icons/RedArrow.png" alt="" /></div>
                  <div style={{ width: isMobile ? 80 : 120 }}><img src="/icons/popupPtag4.png" alt="" /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </Box>
  );
}
